import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Table } from 'app-components';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { formatDate } from '@utils/dates/date.utils';
import { DATE_FORMAT } from '@utils/dates/dateMethods';
import { socialSecurityNumberTools } from '@utils/tools/socialSecurityNumberTools';
import { formatFullName } from '@utils/miscellaneous/formatFullName';
import { IHistoricalHouseholdMembersProps, IHistoricalApplicant } from '../types/historicalApplication.types';

interface ITableRow {
    name: string;
    bewellMemberId: number | string;
    primaryContact: string;
    ssn: string;
    applicationYear: number;
    dob: string;
    eligibleForAptc: boolean;
    csrLevel: string;
}

interface IColumn {
    titleId: string;
    key: string;
    render?: (row: ITableRow) => React.ReactNode;
}

const formatDOB = (dob: string | null): string => {
    if (!dob) return '-';
    return formatDate(dob, DATE_FORMAT);
};

const HistoricalHouseholdMembers: React.FC<IHistoricalHouseholdMembersProps> = ({ 
    applicants, 
    applicationYear, 
    primaryContactFirstName, 
    primaryContactLastName 
}) => {
    const intl = useIntl();

    if (!applicants || applicants.length === 0) {
        return null;
    }

    const primaryContactName = formatFullName(primaryContactFirstName, primaryContactLastName);

    const columns: IColumn[] = [
        {
            titleId: 'label.name',
            key: 'name',
        },
        {
            titleId: 'historicalApp.member_ref_id',
            key: 'bewellMemberId',
        },
        {
            titleId: 'historicalApp.primary_contact',
            key: 'primaryContact',
        },
        {
            titleId: 'historicalApp.ssn',
            key: 'ssn',
        },
        {
            titleId: 'historicalApp.application_year',
            key: 'applicationYear',
        },
        {
            titleId: 'common.dob',
            key: 'dob',
        },
        {
            titleId: 'historicalApp.eligible_for_aptc',
            key: 'eligibleForAptc',
            render: (row: ITableRow) => (
                <span>
                    {row.eligibleForAptc 
                        ? intl.formatMessage({ id: 'common.yes' }) 
                        : intl.formatMessage({ id: 'common.no' })}
                </span>
            ),
        },
        {
            titleId: 'historicalApp.cost_sharing_level',
            key: 'csrLevel',
        },
    ];

    const tableData: ITableRow[] = applicants.map((applicant: IHistoricalApplicant) => ({
        name: formatFullName(applicant.firstName, applicant.lastName),
        bewellMemberId: applicant.bewellMemberId || '-',
        primaryContact: primaryContactName,
        ssn: socialSecurityNumberTools.maskSsnLastFour(applicant.ssnLastFour),
        applicationYear: applicationYear,
        dob: formatDOB(applicant.dob),
        eligibleForAptc: applicant.eligibleForAptc ?? false,
        csrLevel: applicant.csrLevel || '-',
    }));

    return (
        <ConditionalRendering isTrue={applicants.length > 0}>
            <div className="household-members-section margin-top-3">
                <h5 className="margin-bottom-2">
                    <FormattedMessage id="historicalApp.household_members" />
                </h5>
                <div className="historical-application__table-wrapper">
                    <Table 
                        data={tableData} 
                        columns={columns}
                        hasBorders={false}
                    />
                </div>
            </div>
        </ConditionalRendering>
    );
};

export default HistoricalHouseholdMembers;
